/*
Usage Notes: 

The following web component can be dropped into any standard Shopify theme (i.e. non-headless), using Liquid to pass product data:

  {% assign skioSellingPlanGroups = product.selling_plan_groups | where: 'app_id', 'SKIO' %}
  {% if skioSellingPlanGroups.size > 0 %}
    <skio-plan-picker 
      product='{{ product | json | escape }}'
      selectedVariant='{{ product.selected_or_first_available_variant | json | escape }}'
      formId='{{ 'product-form-' | append: section.id }}'
      externalPriceSelector='#price-inner-button .money'
      externalPriceSelectorWithCurrency='[data-product-price-list] .money'
      currency='USD'
      useVariantInputClickEvents='false'
      variantInputSelector=''
      >
    </skio-plan-picker>
    <input type="hidden" aria-hidden="true" name="selling_plan" value="">
    <script src="{{ 'skio-plan-picker-component.js' | asset_url }}" type="module"></script>
    {% render 'skio-details' %}
  {% endif %}

  Note: formId not required if element is inside of a form already

  Example variantChanged event dispatch:
  document.dispatchEvent( new CustomEvent("variantChanged", { detail: { variantId: variant.id } }) );
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js';

const skioDefaultBenefits = [
  'Free shipping',
  '30-day guarantee',
  'Cancel anytime',
  '2x rewards points',
  'Get exclusive deals',
  'Volume Discounts',
];

const skioBenefitCheckIcon = html`<svg
  width="19"
  height="19"
  viewBox="0 0 19 19"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
>
  <path
    d="M8.0625 13.6875L14.6719 7.07812L13.3594 5.76562L8.0625 11.0625L5.39062 8.39062L4.07812 9.70312L8.0625 13.6875ZM9.375 18.75C8.07812 18.75 6.85938 18.5039 5.71875 18.0117C4.57812 17.5195 3.58594 16.8516 2.74219 16.0078C1.89844 15.1641 1.23047 14.1719 0.738281 13.0313C0.246094 11.8906 0 10.6719 0 9.375C0 8.07812 0.246094 6.85938 0.738281 5.71875C1.23047 4.57812 1.89844 3.58594 2.74219 2.74219C3.58594 1.89844 4.57812 1.23047 5.71875 0.738281C6.85938 0.246094 8.07812 0 9.375 0C10.6719 0 11.8906 0.246094 13.0313 0.738281C14.1719 1.23047 15.1641 1.89844 16.0078 2.74219C16.8516 3.58594 17.5195 4.57812 18.0117 5.71875C18.5039 6.85938 18.75 8.07812 18.75 9.375C18.75 10.6719 18.5039 11.8906 18.0117 13.0313C17.5195 14.1719 16.8516 15.1641 16.0078 16.0078C15.1641 16.8516 14.1719 17.5195 13.0313 18.0117C11.8906 18.5039 10.6719 18.75 9.375 18.75ZM9.375 16.875C11.4688 16.875 13.2422 16.1484 14.6953 14.6953C16.1484 13.2422 16.875 11.4688 16.875 9.375C16.875 7.28125 16.1484 5.50781 14.6953 4.05469C13.2422 2.60156 11.4688 1.875 9.375 1.875C7.28125 1.875 5.50781 2.60156 4.05469 4.05469C2.60156 5.50781 1.875 7.28125 1.875 9.375C1.875 11.4688 2.60156 13.2422 4.05469 14.6953C5.50781 16.1484 7.28125 16.875 9.375 16.875Z"
    fill="#373C00"
  />
</svg>`;

const skioStyles = css`
  .skio-plan-picker {
    display: flex;
    flex-direction: column;
    gap: 26px;
    padding: 0;
    border: 0;
    margin-top: 15px;
    max-width: 519px;
  }
  .skio-onetime-second {
    order: 2;
  }

  .skio-group-container {
    display: none;
  }

  .skio-group-container--available {
    display: block;
    position: relative;
    border: 1px solid rgba(55, 60, 0, 1);
    background-color: transparent;
    border-radius: 10px;
    color: rgba(55, 60, 0, 1);
  }
  .skio-group-container--selected {
    color: rgba(55, 60, 0, 1);
    background: rgba(250, 236, 214, 0.8);
    border: 1px solid rgba(55, 60, 0, 1);
  }

  .skio-group-container--available .skio-radio__container {
    color: #373c00;
  }

  .skio-group-container--selected .skio-radio__container {
    color: #373c00;
  }

  .skio-group-container--selected .skio-group-title {
    color: #373c00;
  }

  .skio-group-input {
    position: absolute;
    width: 0px;
    height: 0px;
    opacity: 0;
  }
  .skio-group-input:focus-visible ~ .skio-group-label {
    outline: 2px #ccc solid;
    outline-offset: 4px;
    border-radius: 5px;
  }

  .skio-group-label {
    display: flex;
    cursor: pointer;
    padding: 16px 22px;
    overflow: hidden;
  }

  .skio-group-container--subscribe {
    padding: 41px 22px 28px 22px;
    margin-top: 16.5px;
  }

  .--isCondensed .skio-group-container--subscribe {
    padding: 22px;
  }

  .skio-group-container--subscribe .skio-group-label {
    padding: 0 0 16.5px 0;
    margin-bottom: 16.5px;
    border-bottom: 1px solid #373c00;
  }

  .skio-group-topline {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    font-size: 16px;
  }

  .skio-radio__container {
    display: flex;
    margin-right: 10px;
  }

  .skio-radio {
    transition:
      transform 0.25s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    transform-origin: center;
    transform: scale(0);
    opacity: 0;
  }
  .skio-group-label:hover .skio-radio {
    transform: scale(1);
    opacity: 0.75;
  }
  .skio-group-container--selected .skio-group-label .skio-radio {
    transform: scale(1);
    opacity: 1;
  }

  .skio-price {
    margin-left: auto;
    display: flex;
    flex-direction: row-reverse;
    column-gap: 4px;
    font-size: 18px;
    line-height: 25.02px;
    font-weight: 400;
  }

  .--isCondensed .skio-price {
    font-size: 16px;
    line-height: 22px;
  }

  .skio-subscription-price {
    color: #373c00;
  }

  .skio-onetime-price {
    text-decoration: line-through;
    font-size: 18px;
    line-height: 25.02px;
    font-weight: 400;
    color: rgba(55, 60, 0, 0.5);
  }

  .--isCondensed .skio-onetime-price {
    font-size: 16px;
    line-height: 22px;
  }

  .skio-group-content {
    width: auto;
    opacity: 1;
    order: 3;
  }

  .skio-group-content label {
    font-weight: bold;
  }

  /* Hide frequency if not selected */
  .skio-group-container:not(.skio-group-container--selected) + .skio-group-content {
    max-height: 0;
    opacity: 0;
    pointer-events: none;
  }

  .skio-group-title {
    font-family: RegolaRegular;
    font-size: 18px;
    line-height: 25.2px;
    font-weight: 600;
    color: #373c00;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: calc(100% - 30px);
  }

  .--isCondensed .skio-group-title {
    font-size: 16px;
    line-height: 22px;
  }

  /* .skio-save {
    color: #0fa573;
    border: 1px #0fa573 solid; 
    padding: 0px 8px;
    border-radius: 20px;
  } */

  .skio-frequency {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 0 30px 0 5px;
    text-transform: capitalize;
    border-radius: 0px;
    background-color: transparent;
    width: 100%;
    border: none;
    text-decoration: underline;
    font-size: 14px;
    line-height: 23px;
    font-family: inherit;
    white-space: nowrap;
    outline: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
    background-position: right 10px top 50%;
    background-size: 18px;
    background-repeat: no-repeat;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
  }
  .skio-frequency.skio-frequency--one {
    background-image: none;
    pointer-events: none;
  }

  .skio-frequency span {
    text-transform: lowercase;
  }

  .flex {
    display: flex;
  }

  .sub-benefit-container {
    padding: 0px;
  }

  .sub-benefit-list {
    font-family: RegolaRegular;
    color: inherit;
    padding: 0;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    row-gap: 8px;
    margin: 0;
  }

  .sub-benefit-list li {
    list-style-type: none;
    font-size: 12px;
    line-height: 24px;
    font-weight: 400;
    display: flex;
    align-items: center;
    column-gap: 6px;
    flex: 1 1 50%;
    color: #373c00;
  }

  .sub-benefit-container.--isCondensed .sub-benefit-list li:nth-child(n + 4) {
    display: none;
  }

  .--isCondensed .sub-benefit-list li {
    font-size: 10px;
    line-height: 22px;
    column-gap: 5px;
    flex: 1 1 auto;
  }

  .--isCondensed .sub-benefit-list {
    flex-wrap: nowrap;
    column-gap: 13px;
  }

  .skio-subscription-price-specialOfferText {
    padding: 2px 4px;
    font-size: 10px;
    line-height: 12px;
    font-weight: 700;
    background-color: #5e8092;
    color: #ffffff;
    border: 1px solid #5e8092;
    display: flex;
    align-items: center;
    font-family: RegolaRegular;
    white-space: nowrap;
    margin-left: auto;
  }

  .highlighted-tag {
    position: absolute;
    top: 0;
    transform: translateY(-50%) translateX(-50%);
    left: 50%;
    background: #fc6125;
    border-radius: 81px;
    color: #ffffff;
    padding: 8.1px 12.15px;
    font-size: 9.66px;
    font-weight: 600;
    line-height: 16.1px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    font-family: RegolaRegular;
  }

  .skio-save.--hide {
    display: none;
  }

  .skio-compare-price {
    text-decoration: line-through;
    font-size: 18px;
    line-height: 25.02px;
    font-weight: 400;
    color: rgba(55, 60, 0, 0.5);
  }

  /* Media Queries */
  @media (max-width: 1025px) {
    .skio-group-title {
      font-size: 14px;
    }
    .skio-price {
      font-size: 14px;
    }

    .sub-benefit-list li {
      font-size: 12px;
      line-height: 26px;
    }

    .--isCondensed .skio-group-container--subscribe {
      padding: 16px;
    }
  }
`;

export class SkioPlanPickerComponent extends LitElement {
  static properties = {
    product: { type: Object }, //required
    productHandle: { type: String }, //optional (unless product isn't passed, then required)
    key: { type: String }, //optional, defaults to product.id; identifier for this instance of the Skio plan picker

    formId: { type: String }, //optional; if passed, used to connect input fields to form
    needsFormId: { type: Boolean }, //optional, defaults to false; if true, element needs to be passed a formId, else it searches for a form

    subscriptionFirst: { type: Boolean }, //optional, defaults to false; if true, shows subscription option above onetime
    startSubscription: { type: Boolean }, //optional, defaults to false; if true, auto-selects subscription on page load
    discountFormat: { type: String }, //optional, defaults to percent; can also pass "fixed"

    currency: { type: String }, //optional, defaults to 'USD', but can pass any 3 char identifier
    language: { type: String }, //optional, defaults to 'en-US', but can pass any similarly formatted language identifier
    moneyFormatter: {}, //placeholder for object

    externalPriceSelector: { type: String }, //optional, used to update the external price

    externalPriceSelectorWithCurrency: { type: String }, //optional, used to update the external price

    selectedVariant: { type: Object }, //placeholder for data
    skioSellingPlanGroups: {}, //placeholder for data
    availableSellingPlanGroups: {}, //placeholder for data
    selectedSellingPlanGroup: {}, //placeholder for data
    selectedSellingPlan: {}, //placeholder for data

    defaultFrequency: {}, //placeholder for data

    useVariantInputClickEvents: { type: Boolean }, // optional, allows use of variant input click events to update skio's selectedVariant
    variantInputSelector: {},
    highlightedTag: { type: String }, // optional, property for dynamic highlighted text
    highlightedTagBgColor: { type: String }, // optional,  property for dynamic highlighted background color
    subsPriceColor: { type: String }, // optional, property for subscription price color
    specialOfferText: { type: String }, // optional, property for dynamic special offer text
    specialOfferColor: { type: String }, // optional, property for dynamic special offer text color
    specialOfferBgColor: { type: String }, // optional, property for dynamic special offer Bg color
    subscriptionTitle: { type: String }, // optional, property for dynamic Subscription Title
    oneTimeTitle: { type: String }, // optional, property for dynamic one time purchase
    isCondensed: { type: String }, // optional, allow to show strike price on one time
    benefits: { type: Array }, // optional; per-product benefit labels, falls back to skioDefaultBenefits
  };

  static styles = skioStyles;

  constructor() {
    super();
    this.product = null;
    this.selectedVariant = null;

    this.productHandle = null;

    this.purchaseOption = 'onetime';

    this.key = null;
    this.formId = null;
    this.needsFormId = false;

    this.skioSellingPlanGroups = [];
    this.availableSellingPlanGroups = [];

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.startSubscription = false;
    this.subscriptionFirst = false;

    this.skioMainProduct = true;

    this.discountFormat = 'percent';

    this.externalPriceSelector = '.price-item--regular bdi';

    this.externalPriceSelectorWithCurrency = '[skio-external-price-with-currency]';

    this.currency = Shopify.currency.active;
    this.language = 'en-US';
    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: this.currency,
    });

    this.defaultFrequency = null;

    this.useVariantInputClickEvents = null;
    this.variantInputSelector = null;
    this.highlightedTag = 'Most Popular'; // or any default value
    this.highlightedTagBgColor = '#5E8092';
    this.subsPriceColor = '#5E8092';
    this.specialOfferText = '';
    this.specialOfferColor = '#05A6CC';
    this.specialOfferBgColor = '#E0F2F5';
    this.subscriptionTitle = 'Subscribe & Save';
    this.oneTimeTitle = '30 Days Supply';
    this.isCondensed = 'false';
    this.benefits = skioDefaultBenefits;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.startSubscription == true) {
      this.purchaseOption = 'subscription';
    }

    if (!this.product && this.productHandle) {
      this.fetchProduct(this.productHandle);
    }

    if (this.needsFormId && this.formId == null) {
      let forms = document.querySelectorAll('form[action="/cart/add"]');
      if (forms.length > 0) {
        let form;
        forms.forEach((el) => {
          if (el.hasAttribute('skio-key')) {
            if (el.getAttribute('skio-key') == this.key) form = el;
          }
        });
        if (!form) form = forms[0];
        this.formId = form.id;
        this.requestUpdate();
      }
    }
    let skio = this;
    document.addEventListener('variantChanged', function (e) {
      //update variant id
      let variantId = e.detail.variantId;
      let variant = skio.product.variants.find((x) => x.id == variantId);
      if (variant) skio.selectedVariant = variant;
      else skio.log('Unable to find variant with id: ', variantId);
      skio.requestUpdate();
    });
  }

  render() {
    if (!this.product || !this.selectedVariant || this.skioSellingPlanGroups.length == 0 || !this.product?.available)
      return;

    let skioBenefitItems = (Array.isArray(this.benefits) ? this.benefits : [])
      .map((benefit) => (benefit == null ? '' : String(benefit).trim()))
      .filter((benefit) => benefit.length > 0);
    if (skioBenefitItems.length === 0) skioBenefitItems = skioDefaultBenefits;
    const skioBenefitsList = skioBenefitItems.map((benefit) => html`<li>${skioBenefitCheckIcon}${benefit}</li>`);

    return html` <fieldset
      class="skio-plan-picker ${this.isCondensed == 'true' ? '--isCondensed' : ''}"
      skio-plan-picker="${this.key}"
    >
      <input
        ${this.formId !== null ? html`form="${this.formId}"` : ''}
        name="selling_plan"
        type="hidden"
        value="${this.selectedSellingPlan !== null ? this.selectedSellingPlan?.id : ''}"
      />
      <input
        ${this.formId !== null ? html`form="${this.formId}"` : ''}
        name="properties[Discount]"
        type="hidden"
        value="${this.selectedSellingPlan !== null
          ? this.calculateDynamicDiscount(this.selectedSellingPlan).percent
          : ''}"
        ?disabled="${this.selectedSellingPlan == null ? true : false}"
      />

      <div
        class="skio-group-container skio-group-container--one-time ${this.product.requires_selling_plan == false
          ? 'skio-group-container--available'
          : ''} ${this.selectedSellingPlanGroup == null ? 'skio-group-container--selected' : ''} ${this
          .subscriptionFirst
          ? 'skio-onetime-second'
          : ''}"
        skio-group-container
        @click=${() => this.selectSellingPlanGroup(null)}
      >
        <input
          id="skio-one-time-${this.key}"
          class="skio-group-input"
          name="skio-group-${this.key}"
          type="radio"
          value=""
          skio-one-time
          ?checked=${this.startSubscription == false && this.product.requires_selling_plan == false ? true : false}
        />
        <label class="skio-group-label" for="skio-one-time-${this.key}">
          <div class="skio-group-topline">
            <div class="skio-radio__container">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1"></circle>
                <circle class="skio-radio" cx="12" cy="12" r="7" fill="currentColor"></circle>
              </svg>
            </div>
            <div class="skio-group-title">
              ${this.oneTimeTitle}${this.shouldShowComparePrice()
                ? html` (${this.discountFormat == 'percent'
                    ? this.calculateDynamicDiscount(null).percent
                    : this.calculateDynamicDiscount(null).amount}
                  OFF)`
                : html``}
              <div class="skio-price">
                ${this.shouldShowComparePrice()
                  ? html`<span class="skio-compare-price">${this.compareAtPrice()}</span>`
                  : html``}

                <span skio-onetime-price>${this.money(Math.round(this.selectedVariant.price))}</span>
              </div>
            </div>
          </div>
        </label>
      </div>
      ${this.availableSellingPlanGroups
        ? this.availableSellingPlanGroups.map(
            (group, index) => html`
              <div
                class="skio-group-container skio-group-container--subscribe skio-group-container--available ${this
                  .selectedSellingPlanGroup == group
                  ? 'skio-group-container--selected'
                  : ''}"
                skio-group-container
                @click=${() => this.selectSellingPlanGroup(group)}
              >
                ${this.highlightedTag
                  ? html`<span class="highlighted-tag" style="background: ${this.highlightedTagBgColor};">
                      ${this.highlightedTag}
                    </span>`
                  : html``}
                <input
                  id="skio-selling-plan-group-${index}-${this.key}"
                  class="skio-group-input"
                  name="skio-group-${this.key}"
                  type="radio"
                  value="${group.id}"
                  skio-selling-plan-group="${group.id}"
                  ?checked=${this.selectedSellingPlanGroup == group ? true : false}
                />
                <label class="skio-group-label" for="skio-selling-plan-group-${index}-${this.key}">
                  <div class="skio-group-topline">
                    <div class="skio-radio__container">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1"></circle>
                        <circle class="skio-radio" cx="12" cy="12" r="7" fill="currentColor"></circle>
                      </svg>
                    </div>
                    <div class="skio-group-title">
                      ${this.subscriptionTitle.replace(/ \d+% Off$/, '')}${this.calculateDynamicDiscount(
                        group.selected_selling_plan,
                      ).percent !== '0%'
                        ? html` (${this.discountFormat == 'percent'
                            ? this.calculateDynamicDiscount(group.selected_selling_plan).percent
                            : this.calculateDynamicDiscount(group.selected_selling_plan).amount}
                          OFF)`
                        : html``}&nbsp;
                      ${this.calculateDynamicDiscount(group.selected_selling_plan).percent !== '0%'
                        ? html` <span class="skio-save --hide">
                            <span skio-discount
                              >${this.discountFormat == 'percent'
                                ? this.calculateDynamicDiscount(group.selected_selling_plan).percent
                                : this.calculateDynamicDiscount(group.selected_selling_plan).amount}</span
                            ></span
                          >`
                        : html``}

                      <div class="skio-price">
                        ${this.shouldShowComparePrice()
                          ? html`<span class="skio-compare-price">${this.compareAtPrice()}</span>`
                          : this.calculateDynamicDiscount(group.selected_selling_plan).percent !== '0%'
                            ? html`
                                <span class="skio-onetime-price" skio-onetime-price>
                                  ${this.money(Math.round(this.selectedVariant.price))}
                                </span>
                              `
                            : html``}
                        <span
                          class="skio-subscription-price"
                          style="color: ${this.subsPriceColor};"
                          skio-subscription-price
                        >
                          ${this.price(group.selected_selling_plan)}
                        </span>
                      </div>
                    </div>
                    ${this.specialOfferText
                      ? html`<span
                          class="skio-subscription-price-specialOfferText"
                          style="color: ${this.specialOfferColor}; background: ${this.specialOfferBgColor};"
                          >${this.specialOfferText}</span
                        >`
                      : html``}
                  </div>
                </label>
                <div class="sub-benefit-container ${this.isCondensed == 'true' ? '--isCondensed' : ''}">
                  <ul class="sub-benefit-list">
                    ${skioBenefitsList}
                  </ul>
                </div>
              </div>
            `,
          )
        : ''}
    </fieldset>`;
  }

  updated = (changed) => {
    if (changed.has('product') && this.product) {
      //update key
      this.key = this.key ? this.key : this.product.id;

      //update skioSellingPlanGroups
      this.skioSellingPlanGroups = this.product.selling_plan_groups.filter(
        (selling_plan_group) => selling_plan_group.app_id === 'SKIO',
      );

      this.skioSellingPlanGroups.forEach((group) => {
        group.selling_plans.sort(function (a, b) {
          if (parseInt(a.name.replace(/\D/g, '')) < parseInt(b.name.replace(/\D/g, ''))) return -1;
          if (parseInt(a.name.replace(/\D/g, '')) > parseInt(b.name.replace(/\D/g, ''))) return 1;
          if (parseInt(a.name.replace(/\D/g, '')) == parseInt(b.name.replace(/\D/g, ''))) return 0;
        });
      });
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      let skioSellingPlanGroups = JSON.parse(JSON.stringify(this.skioSellingPlanGroups));

      this.availableSellingPlanGroups = skioSellingPlanGroups.filter((selling_plan_group) =>
        selling_plan_group.selling_plans.some((selling_plan) =>
          this.selectedVariant.selling_plan_allocations.some(
            (selling_plan_allocation) => selling_plan_allocation.selling_plan_id === selling_plan.id,
          ),
        ),
      );

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length > 0) {
        //update each group with a default selected_selling_plan
        this.availableSellingPlanGroups.forEach((group) => {
          group.selling_plans = group.selling_plans.filter((x) =>
            this.selectedVariant.selling_plan_allocations.find((y) => y.selling_plan_id == x.id),
          );
        });

        this.availableSellingPlanGroups.forEach((group) => {
          if (this.defaultFrequency) {
            let selling_plan = group.selling_plans.find((x) =>
              x.name.toLowerCase().includes(this.defaultFrequency.toLowerCase()),
            );
            if (selling_plan) group.selected_selling_plan = selling_plan;
            else group.selected_selling_plan = group.selling_plans[0];
          } else {
            group.selected_selling_plan = group.selling_plans[0];
          }
        });

        if (
          this.startSubscription == true ||
          this.product.requires_selling_plan == true ||
          this.purchaseOption == 'subscription'
        ) {
          //find a matching selling plan, or choose first available

          if (this.selectedSellingPlan == null || this.selectedSellingPlan == undefined) {
            this.selectSellingPlanGroup(this.availableSellingPlanGroups[0]);
          }

          let sellingPlanName = this.selectedSellingPlan?.name;

          let sellingPlanGroup = this.availableSellingPlanGroups.find((x) =>
            x.selling_plans.find((y) => y.name == sellingPlanName),
          );
          let sellingPlan = sellingPlanGroup?.selling_plans.find((y) => y.name == sellingPlanName);
          if (sellingPlanName == sellingPlan?.name) {
            this.selectedSellingPlanGroup = sellingPlanGroup;
            this.selectedSellingPlan = sellingPlan;
          } else {
            this.selectedSellingPlanGroup = this.availableSellingPlanGroups[0];
            this.selectedSellingPlan = this.availableSellingPlanGroups[0].selling_plans[0];
          }
        } else {
          (this.selectedSellingPlan, (this.selectedSellingPlanGroup = null));
        }
      } else {
        (this.selectedSellingPlan, (this.selectedSellingPlanGroup = null));
      }

      //update the form that was passed, if any
      this.updateForm();
    }

    if (changed.has('selectedSellingPlan')) {
      //update price of price elements if applicable
      document.querySelectorAll(`[skio-price][skio-key="${this.key}"]`).forEach((el) => {
        el.innerHTML = this.price(this.selectedSellingPlan);
      });

      //update display of external content elements
      document.querySelectorAll(`[skio-onetime-content][skio-key="${this.key}"]`).forEach((el) => {
        this.selectedSellingPlan !== null ? (el.style.display = 'none') : el.style.removeProperty('display');
      });

      document.querySelectorAll(`[skio-subscription-content][skio-key="${this.key}"]`).forEach((el) => {
        this.selectedSellingPlan == null ? (el.style.display = 'none') : el.style.removeProperty('display');
      });

      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true,
        composed: true,
        detail: {
          sellingPlan: this.selectedSellingPlan,
          key: this.key,
        },
      });

      this.dispatchEvent(event);

      //update the form that was passed, if any
      this.updateForm();
      this.updateExternalPrice();

      //update external selling_plan input value
      let sellingPlanInput = document.querySelector('input[name="selling_plan"]');
      this.selectedSellingPlan != null
        ? (sellingPlanInput.value = this.selectedSellingPlan.id)
        : (sellingPlanInput.value = '');
    }

    if (changed.has('formId')) {
      //update the form that was passed, if any
      this.updateForm();
    }
  };

  log = (...args) => {
    args.unshift('%c[skio]', 'color: #8770f2;');
    console.log.apply(console, args);
  };

  error = (...args) => {
    args.unshift('%c [skio]', 'color: #ff0000');
    console.error.apply(console, args);
  };

  updateExternalPrice() {
    document.querySelectorAll('dd:not(.price__compare) .price-item bdi').forEach((el) => {
      el.innerHTML = this.price(this.selectedSellingPlan);
    });
    document.querySelectorAll(this.externalPriceSelectorWithCurrency).forEach((el) => {
      el.innerHTML = this.price(this.selectedSellingPlan) + ' ' + this.currency;
    });
  }

  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;
    if (this.selectedSellingPlan) this.lastSellingPlanName = this.selectedSellingPlan.name;
    if (group) this.purchaseOption = 'subscription';
    else this.purchaseOption = 'onetime';

    //update the form that was passed, if any
    this.updateForm();
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(element, group) {
    let selling_plan = group.selling_plans.find((x) => x.id == element.value);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
      this.lastSellingPlanName = this.selectedSellingPlan.name;
    } else
      this.log(
        "Error: couldn't find selling plan with id " +
          element.value +
          ' for variant ' +
          this.selectedVariant.id +
          ' from product ' +
          this.product.id +
          ' : ' +
          this.product.handle,
      );
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlanButton(plan, group) {
    let selling_plan = group.selling_plans.find((x) => x.id == plan.id);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
      this.lastSellingPlanName = this.selectedSellingPlan.name;
    } else
      this.log(
        "Error: couldn't find selling plan with id " +
          element.value +
          ' for variant ' +
          this.selectedVariant.id +
          ' from product ' +
          this.product.id +
          ' : ' +
          this.product.handle,
      );
  }

  // Formats integer value into money value
  money(price) {
    // Round to nearest dollar (100 cents) to avoid decimal values
    const roundedPrice = Math.round(price / 100) * 100;
    const formatted = this.moneyFormatter.format(roundedPrice / 100.0);
    // Remove .00 from the end
    return formatted.replace(/\.00(?=\D*$)/, '');
  }

  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discount(selling_plan) {
    if (!selling_plan) return { percent: '0%', amount: 0 };

    const price_adjustment = selling_plan.price_adjustments[0];
    const discount = { percent: '0%', amount: 0 };
    const price = this.selectedVariant.price;

    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = `${price_adjustment.value}%`;
        discount.amount = Math.round((price * price_adjustment.value) / 100.0);
        break;
      case 'fixed_amount':
        discount.percent = `${Math.round(((price_adjustment.value * 1.0) / price) * 100.0)}%`;
        discount.amount = price_adjustment.value;
        break;
      case 'price':
        discount.percent = `${Math.round((((price - price_adjustment.value) * 1.0) / price) * 100.0)}%`;
        discount.amount = price - price_adjustment.value;
        break;
    }

    return discount;
  }

  // Calculates the variant's price for the given selling plan, returns a formatted money value (if desired)
  price(selling_plan, formatted = true) {
    const calculatedPrice = this.selectedVariant.price - this.discount(selling_plan).amount;
    return formatted ? this.money(Math.round(calculatedPrice)) : Math.round(calculatedPrice);
  }

  // Gets the compare-at-price for the current variant, returns formatted money value
  compareAtPrice(formatted = true) {
    if (!this.selectedVariant.compare_at_price || this.selectedVariant.compare_at_price <= this.selectedVariant.price) {
      return null;
    }
    return formatted
      ? this.money(Math.round(this.selectedVariant.compare_at_price))
      : Math.round(this.selectedVariant.compare_at_price);
  }

  // Checks if compare-at-price should be shown
  shouldShowComparePrice() {
    return this.selectedVariant.compare_at_price && this.selectedVariant.compare_at_price > this.selectedVariant.price;
  }

  // Calculates dynamic discount percentage between compare-at-price and final price
  calculateDynamicDiscount(selling_plan = null) {
    if (!this.shouldShowComparePrice()) {
      const fallbackDiscount = this.discount(selling_plan);
      return {
        percent: fallbackDiscount.percent,
        amount: this.money(fallbackDiscount.amount),
      };
    }

    const comparePrice = this.selectedVariant.compare_at_price;
    const finalPrice = selling_plan
      ? Math.round(this.selectedVariant.price - this.discount(selling_plan).amount)
      : Math.round(this.selectedVariant.price);

    const discountAmount = Math.round(comparePrice - finalPrice);
    const discountPercent = Math.round((discountAmount / comparePrice) * 100);

    return {
      percent: `${discountPercent}%`,
      amount: this.money(discountAmount),
    };
  }

  // If a formId was passed, appends the necessary <input> elements to the form
  updateForm() {
    if (!this.formId) return;

    const targetFormIds = [this.formId, `${this.formId}--alt`];

    targetFormIds.forEach((id) => {
      const form = document.querySelector(`#${id}`);
      if (!form) return;

      let selling_plan_input = form.querySelector('[name="selling_plan"]');
      if (selling_plan_input) {
        selling_plan_input.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null;
        selling_plan_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
      } else {
        selling_plan_input = document.createElement('input');
        selling_plan_input.type = 'hidden';
        selling_plan_input.name = 'selling_plan';
        selling_plan_input.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null;
        selling_plan_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
        form.append(selling_plan_input);
      }

      let discount_input = form.querySelector('[name="properties[Discount]"]');
      if (discount_input) {
        discount_input.value =
          this.selectedSellingPlan?.id !== undefined
            ? this.calculateDynamicDiscount(this.selectedSellingPlan).percent
            : null;
        discount_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
      } else {
        discount_input = document.createElement('input');
        discount_input.type = 'hidden';
        discount_input.name = 'properties[Discount]';
        discount_input.value =
          this.selectedSellingPlan?.id !== undefined
            ? this.calculateDynamicDiscount(this.selectedSellingPlan).percent
            : null;
        discount_input.disabled = this.selectedSellingPlan?.id !== undefined ? false : true;
        form.append(discount_input);
      }
    });
  }

  addVariantClickEventListeners() {
    let variantInputs = document.querySelectorAll(this.variantInputSelector);
    let skio = this;
    for (let el of variantInputs) {
      el.addEventListener('click', function (e) {
        // may need to replace with ID / e.target depending on client setup
        // may need to use different attribute depending on ^^
        let variantTitle = e.currentTarget.value;
        skio.selectedVariant = skio.product.variants.find((variant) => variant.title == variantTitle);
      });
    }
  }

  // Optional functions keep if necessary

  /**
   *
   *
   */

  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && { selling_plan: this.selectedSellingPlan?.id }),
      },
    ];

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    })
      .then((response) => response.json())
      .then((response) => {
        this.log('SKIO added item to cart: ', response);
        //dispatch CustomEvent to tell document that an item was added to cart
        const event = new CustomEvent(`skio::added-to-cart`, {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key,
          },
        });

        this.dispatchEvent(event);
      })
      .catch((error) => {
        this.error(`SKIO ${this.key} error adding item to cart: `, error);
      });
  }

  fetchProduct = (handle) => {
    return fetch(`/products/${handle}.js`)
      .then((response) => response.json())
      .then((product) => {
        this.product = product;
        this.selectedVariant = product.variants[0];

        return product;
      });
  };
}

customElements.define('skio-plan-picker', SkioPlanPickerComponent);
